/**
 * Inventory CSV export helpers (feature/inventory-export).
 *
 * Pure, DOM-free builders + a thin browser download wrapper. The page passes
 * the already-filtered inventory rows (all matches across pages, NOT just the
 * current page) into buildInventoryCsv; this module only serializes them.
 *
 * CSV target is German/Swiss Excel:
 *   - UTF-8 BOM prefix so Excel on Windows renders Umlaute (MÄLLEN, LURÖY, …)
 *   - ";" field separator (de-CH/Excel convention)
 *   - RFC-4180 style quoting for fields containing ; " or newlines
 *   - numeric raw values (quantities, price) written as plain numbers, never
 *     as formatted "CHF ..." strings — "CHF" lives in the header only.
 */

/** German label for the raw InventoryItem.status; unknown → raw value. */
const INVENTORY_STATUS_LABELS_DE = {
  stored: "Lager",
  damaged: "Beschädigt",
  repair: "In Reparatur",
  retired: "Ausgemustert",
  active: "Aktiv",
  // Raw value of the write-off path. Only a label: no new semantics, no
  // writer, and no colour of its own -- the list keeps "disposed" visually
  // neutral (InventoryList.jsx STATUS_TONE) because the write-off model is
  // still open. Without this entry the S5 list would print the raw English
  // "disposed" at the user, which is the one thing it must not do.
  disposed: "Entsorgt",
};

export function inventoryStatusLabelDe(status) {
  const s = status == null ? "" : String(status).trim();
  if (!s) return "";
  return INVENTORY_STATUS_LABELS_DE[s] || s;
}

/**
 * Column definition for the export. Order here is the exact wire order.
 * numeric=true → raw number, unquoted, no thousands/CHF formatting.
 */
export const INVENTORY_EXPORT_COLUMNS = [
  { header: "Nr.", get: (r) => r.inventory_number },
  { header: "Name", get: (r) => r.name },
  { header: "Kategorie", get: (r) => r.category },
  { header: "Lief.-Nr.", get: (r) => r.supplier_article_number },
  { header: "Bezug", get: (r) => r.purchased_from },
  { header: "Produkt-Link", get: (r) => r.product_url },
  { header: "Gesamt", numeric: true, get: (r) => r.total_quantity },
  { header: "Zugeordnet", numeric: true, get: (r) => r.assigned_total },
  { header: "Frei", numeric: true, get: (r) => r.available },
  { header: "Anschaffung (CHF)", numeric: true, get: (r) => r.purchase_price_chf },
  { header: "Status", get: (r) => inventoryStatusLabelDe(r.status) },
  { header: "Zustand", get: (r) => r.condition },
  { header: "Kaufdatum", get: (r) => r.purchase_date },
  // Room filter (feature/inventory-room-filter): quantity in the selected room.
  // Empty when no room filter is active (row.room_quantity undefined).
  { header: "Menge im Raum", numeric: true, get: (r) => r.room_quantity },
];

/** Quote one text cell if it contains the separator, a quote, or a newline. */
export function csvTextCell(value) {
  if (value == null) return "";
  const s = String(value);
  if (s === "") return "";
  if (/[";\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Numeric cell → plain number string (raw value); null/""/NaN → empty. */
export function csvNumericCell(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return String(n);
}

const CSV_SEPARATOR = ";";
const CSV_EOL = "\r\n";
const CSV_BOM = String.fromCharCode(0xFEFF); // UTF-8 BOM

/**
 * Build the full CSV document (incl. BOM + header) from inventory rows.
 * Rows are expected to be the raw item dicts from GET /admin/inventory.
 */
export function buildInventoryCsv(items) {
  const rows = Array.isArray(items) ? items : [];
  const headerLine = INVENTORY_EXPORT_COLUMNS.map((c) => csvTextCell(c.header)).join(
    CSV_SEPARATOR
  );
  const bodyLines = rows.map((row) =>
    INVENTORY_EXPORT_COLUMNS.map((c) =>
      c.numeric ? csvNumericCell(c.get(row)) : csvTextCell(c.get(row))
    ).join(CSV_SEPARATOR)
  );
  return CSV_BOM + [headerLine, ...bodyLines].join(CSV_EOL) + CSV_EOL;
}

/** "inventar-export-YYYY-MM-DD.csv" from a local Date (defaults to now). */
export function inventoryExportFilename(now = new Date(), ext = "csv") {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `inventar-export-${y}-${m}-${d}.${ext}`;
}

/**
 * Serialize + trigger a browser download of the inventory CSV. DOM side-effect
 * only; the pure builders above are what the unit tests exercise.
 */
export function downloadInventoryCsv(items, { now, filename } = {}) {
  const csv = buildInventoryCsv(items);
  const name = filename || inventoryExportFilename(now || new Date());
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
  return name;
}
